import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  BOARD_STATUSES,
  type CreateTaskInput,
  type Task,
  type TaskPatch,
  type TaskStatus,
} from '../../api/organizer';
import { BoardColumn } from './BoardColumn';
import { TaskCard } from './TaskCard';
import { groupByStatus } from './taskUtils';

interface TaskBoardProps {
  tasks: Task[];
  pendingIds: Set<string>;
  onPatch: (taskId: string, patch: TaskPatch) => void;
  onCreate: (input: CreateTaskInput) => void;
  onOpen: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  onMove: (args: {
    taskId: string;
    status: TaskStatus;
    beforeId: string | null;
    afterId: string | null;
  }) => void;
}

type ColumnMap = Record<TaskStatus, string[]>;

function toColumnMap(tasks: Task[]): ColumnMap {
  const grouped = groupByStatus(tasks);
  return Object.fromEntries(
    BOARD_STATUSES.map((status) => [status, (grouped[status] ?? []).map((t) => t.id)]),
  ) as ColumnMap;
}

function statusOf(columns: ColumnMap, taskId: string): TaskStatus | undefined {
  return BOARD_STATUSES.find((status) => columns[status]?.includes(taskId));
}

/**
 * The board, and the only place drag state lives.
 *
 * During a drag the column order is held locally so the card can preview its new
 * position on every hover without firing a request per hover. Exactly one
 * mutation goes out, on drop.
 */
export function TaskBoard({
  tasks,
  pendingIds,
  onPatch,
  onCreate,
  onOpen,
  onDelete,
  onMove,
}: TaskBoardProps) {
  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const serverColumns = useMemo(() => toColumnMap(tasks), [tasks]);

  const [columns, setColumns] = useState<ColumnMap>(serverColumns);
  const [activeId, setActiveId] = useState<string | null>(null);
  const dragging = activeId !== null;

  // Adopt the server's order, but never mid-drag: a background refetch landing
  // while a card is in the air would yank it back under the cursor.
  const draggingRef = useRef(false);
  draggingRef.current = dragging;
  useEffect(() => {
    if (!draggingRef.current) setColumns(serverColumns);
  }, [serverColumns]);

  const sensors = useSensors(
    // 4px of slop before a drag starts, or the checkbox, date picker and inline
    // title editor inside each card become impossible to click.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function resolveTargetStatus(overId: string): TaskStatus | undefined {
    if (overId.startsWith('column:')) return overId.slice('column:'.length) as TaskStatus;
    return statusOf(columns, overId);
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  /** Live preview only — local state, no network. */
  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;

    const taskId = String(active.id);
    const from = statusOf(columns, taskId);
    const to = resolveTargetStatus(String(over.id));
    if (!from || !to) return;

    if (from === to) {
      const list = columns[from] ?? [];
      const oldIndex = list.indexOf(taskId);
      const overIndex = String(over.id).startsWith('column:')
        ? list.length - 1
        : list.indexOf(String(over.id));
      if (oldIndex === -1 || overIndex === -1 || oldIndex === overIndex) return;

      const next = [...list];
      next.splice(oldIndex, 1);
      next.splice(overIndex, 0, taskId);
      setColumns((prev) => ({ ...prev, [from]: next }));
      return;
    }

    // Crossing columns: pull it out of the old one and insert at the hovered
    // position in the new one.
    const target = [...(columns[to] ?? [])];
    const overIndex = String(over.id).startsWith('column:')
      ? target.length
      : target.indexOf(String(over.id));
    const insertAt = overIndex === -1 ? target.length : overIndex;
    target.splice(insertAt, 0, taskId);

    setColumns((prev) => ({
      ...prev,
      [from]: (prev[from] ?? []).filter((id) => id !== taskId),
      [to]: target,
    }));
  }

  function handleDragEnd(event: DragEndEvent) {
    const taskId = String(event.active.id);
    setActiveId(null);

    const status = statusOf(columns, taskId);
    if (!status) {
      setColumns(serverColumns);
      return;
    }

    const list = columns[status] ?? [];
    const index = list.indexOf(taskId);
    const withoutMoved = list.filter((id) => id !== taskId);

    const beforeId = withoutMoved[index - 1] ?? null;
    const afterId = withoutMoved[index] ?? null;

    // Nothing actually changed — don't spend a request on a click that happened
    // to travel five pixels.
    const original = serverColumns[status] ?? [];
    if (original.indexOf(taskId) === index && statusOf(serverColumns, taskId) === status) return;

    onMove({ taskId, status, beforeId, afterId });
  }

  const activeTask = activeId ? byId.get(activeId) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveId(null);
        setColumns(serverColumns);
      }}
    >
      {/* Every column gets the same width, empty or not, so the board doesn't
          reflow as cards move. Card order is the manual drag order. */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        {BOARD_STATUSES.map((status) => (
          <BoardColumn
            key={status}
            status={status}
            tasks={(columns[status] ?? []).map((id) => byId.get(id)).filter((t): t is Task => Boolean(t))}
            pendingIds={pendingIds}
            onPatch={onPatch}
            onCreate={onCreate}
            onOpen={onOpen}
            onDelete={onDelete}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTask && (
          <TaskCard
            task={activeTask}
            isOverlay
            onPatch={() => {}}
            onOpen={() => {}}
            onDelete={() => {}}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}
